import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { formatNorwegianDate } from '../utils/formatters';

interface ReferenceItem {
  header: string;
  value: string;
}

interface DataDetailsProps {
  docNumber: string;
  guideVersionPublicId: string;
  publishedDate?: string;
  imgName?: string;
  small?: boolean;
  large?: boolean;
}

const formatDocNumber = (doc: string): string => {
  return doc.length >= 3 ? `${doc.slice(0, 3)}.${doc.slice(3)}` : doc;
};

const formatImgName = (imgName?: string): string => {
  if (!imgName) return '';
  // Remove "Fig." prefix (case-insensitive, with optional period and space)
  return imgName.replace(/^Fig\.?\s*/i, '');
};

const DataDetails: React.FC<DataDetailsProps> = ({ docNumber, guideVersionPublicId, publishedDate, imgName, small = false, large = false }) => {
  const referenceItems: ReferenceItem[] = [
    { header: 'FIG', value: formatImgName(imgName) },
    { header: 'ANVISNING', value: formatDocNumber(docNumber) },
    { header: 'VERSJON', value: guideVersionPublicId },
    { header: 'PUBLISERT', value: formatNorwegianDate(publishedDate) ?? '' },
  ];

  if (small) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '30px 60px 50px 75px',
          alignItems: 'start',
          columnGap: 0.5,
          flexShrink: 0,
          width: '100%',
          minWidth: 0,
        }}>
        {referenceItems.map(({ header, value }, index) => (
          <Box
            key={header}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
            }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.6rem',
                fontWeight: 600,
                color: 'text.secondary',
                mb: '1px',
                lineHeight: 1.1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: index === 0 ? 'left' : undefined,
              }}>
              {header}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: index === 0 ? 'left' : undefined,
              }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content repeat(3, minmax(0, max-content))',
        columnGap: large ? 0.75 : 0.5,
        mb: large ? 0.75 : 0.5,
        pt: large ? 0.75 : 0.5,
        width: '100%',
        minWidth: 0,
      }}>
      {referenceItems.map(({ header, value }, index) => (
        <Box
          key={header}
          sx={{
            minWidth: 0,
            maxWidth: index === 0 ? (large ? '80px' : '60px') : 'none',
            overflow: 'hidden',
          }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: large ? '0.75rem' : '0.65rem',
              fontWeight: large ? 500 : 700,
              color: 'text.secondary',
              mb: large ? '1.5px' : '1px',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: index === 0 ? 'left' : undefined,
            }}>
            {header}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: large ? '0.85rem' : '0.75rem',
              fontWeight: large ? 500 : 700,
              color: 'text.secondary',
              lineHeight: 1.1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: index === 0 ? 'left' : undefined,
            }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default DataDetails;
